// src/pages/SocialOverlay.jsx
// Dirette social simulate per i video promozionali: l'interfaccia di Instagram
// Live o YouTube Live disegnata sopra un fondo VERDE, così in montaggio si
// sostituisce la clip che si vuole, e insieme le grafiche vere di RaceSense
// (regia automatica + striscia sponsor) collegate al WebSocket di gara.
//
//   /obs/social/instagram           → 9:16 (Browser Source 1080x1920)
//   /obs/social/youtube             → 16:9 (Browser Source 1920x1080)
//   /obs/social/youtube-verticale   → 9:16, app YouTube da telefono
//
// Query:
//   ?user=racesense_official   nome dell'account (Instagram)
//   ?channel=RaceSense Live    nome del canale (YouTube)
//   ?title=...                 titolo del video (YouTube)
//   ?viewers=1523              spettatori iniziali (poi oscillano da soli)
//   ?comments=3                secondi fra un commento finto e l'altro. Attivi solo
//                              su YouTube da PC; nelle verticali si riaccendono
//                              passando un valore maggiore di zero
//   ?graphics=0                toglie la regia automatica RaceSense
//   ?ticker=0                  toglie la striscia sponsor
//   ?bg=green|transparent|dark fondo al posto del verde
//   ?scrim=1                   sfumature scure sopra il video (NON adatte al chroma key)
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AutoGraphics } from './ObsGraphics';
import { SponsorTicker } from './SponsorOverlay';
import raceSenseLogo from '../img/IOS1024_BG.png';
import './SocialOverlay.css';

const IG_W = 540, IG_H = 960;      // palco logico verticale (x2 = 1080x1920)
const YT_W = 1280, YT_H = 720;     // palco logico orizzontale (x1.5 = 1920x1080)

/* ---------------------------------------------------------------------------
   Palco scalato: si disegna a misura fissa e si adatta alla Browser Source.
--------------------------------------------------------------------------- */
function Stage({ w, h, className = '', children }) {
  const fit = () => (typeof window === 'undefined' ? 1 : Math.max(0.05, Math.min(window.innerWidth / w, window.innerHeight / h)));
  const [scale, setScale] = useState(fit);
  useEffect(() => {
    const onResize = () => setScale(fit());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w, h]);
  return (
    <div className={`sc-stage ${className}`} style={{ width: w, height: h, transform: `translate(-50%, -50%) scale(${scale})` }}>
      {children}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Pubblico finto: nomi, commenti e reazioni credibili da diretta di karting.
--------------------------------------------------------------------------- */
const USERS = [
  'marco_kart22', 'giuly.rossi', 'ale_speed7', 'ilaria.p', 'kartlover88', 'dave_motorsport',
  'sara.rcs', 'nico_trackday', 'fra_endurance', 'teo.racing', 'luca__b', 'martina_gp',
  'andrea.pit', 'ste_apex', 'chiara_k', 'paolo.rossi91', 'vale_rossa', 'matteo_drs',
  'giorgia.trackside', 'simo_racing', 'the_kart_guy', 'elena__m', 'riky.braking', 'gabri_p3',
];
const COMMENTS = [
  'Che ritmo!!', 'Forza ragazzi!!', 'Che sorpasso pazzesco', 'Ma quanto va il 60?', 'Seguo da Bergamo',
  'Best lap incredibile', 'Il 22 sta recuperando', 'Dai dai dai', 'Che spettacolo il live timing',
  'Quanto manca alla fine?', 'Adesso il cambio pilota?', 'Bravissimi!', 'Sto guardando dal lavoro',
  'Grande RaceSense!', 'Che battaglia per il podio', 'Il mio team e primo!', 'Gomme finite secondo me',
  'Si vede tutto benissimo', 'Che tempi!!', 'In pista si soffre', 'Chi e in testa adesso?',
  'Bellissima questa grafica', 'Vai cosi 111!', 'Il settore 2 e suo', 'Mamma mia che passo',
  'Un boato in pista', 'Seguo dal box', 'Prossimo pit tra poco', 'Ma il 7 ha forato?',
  'Tre decimi di distacco, assurdo', 'Il rosso sta spingendo', 'Che regolarita', 'Occhio alla bandiera gialla',
];
const SUPER_TEXTS = ['Forza ragazzi!!', 'Grande gara!', 'Offro il caffe ai meccanici', 'Top il live timing', 'Dai che si vince!'];
const SUPER_AMOUNTS = ['2,00 €', '5,00 €', '10,00 €', '20,00 €', '50,00 €'];
const AVATAR_COLORS = [
  ['#f09433', '#dc2743'], ['#4f5bd5', '#962fbf'], ['#00c6ff', '#0072ff'], ['#f7971e', '#ffd200'],
  ['#11998e', '#38ef7d'], ['#fc466b', '#3f5efb'], ['#ff512f', '#dd2476'], ['#396afc', '#2948ff'],
];
const hashOf = (s) => String(s).split('').reduce((a, c) => ((a * 33) + c.charCodeAt(0)) >>> 0, 5381);
const pick = (arr, seed) => arr[seed % arr.length];
const rnd = (a, b) => a + Math.random() * (b - a);

function Avatar({ name, size = 32, className = '' }) {
  const h = hashOf(name);
  const [c1, c2] = pick(AVATAR_COLORS, h);
  const initials = String(name).replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase() || '··';
  return (
    <span
      className={`sc-avatar ${className}`}
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${c1}, ${c2})`, fontSize: Math.round(size * 0.36) }}
    >
      {initials}
    </span>
  );
}

/**
 * Flusso di messaggi finti. `kinds` decide quali eventi possono comparire:
 * 'msg' commento, 'join' ingresso, 'badge' badge Instagram, 'super' Super Chat.
 */
function useFakeChat({ everySec = 3, max = 5, kinds = ['msg'], enabled = true, seed = 4 }) {
  const idRef = useRef(0);
  const [items, setItems] = useState(() => Array.from({ length: enabled && everySec > 0 ? Math.min(seed, max) : 0 }, () => ({
    id: ++idRef.current,
    user: pick(USERS, Math.floor(Math.random() * 10000)),
    kind: 'msg',
    text: pick(COMMENTS, Math.floor(Math.random() * 10000)),
    superText: pick(SUPER_TEXTS, Math.floor(Math.random() * 10000)),
    amount: pick(SUPER_AMOUNTS, Math.floor(Math.random() * 10000)),
  })));
  useEffect(() => {
    if (!enabled || everySec <= 0) return undefined;
    let timer = null;
    const push = () => {
      const user = pick(USERS, Math.floor(Math.random() * 10000));
      const roll = Math.random();
      let kind = 'msg';
      if (kinds.includes('badge') && roll > 0.93) kind = 'badge';
      else if (kinds.includes('super') && roll > 0.94) kind = 'super';
      else if (kinds.includes('join') && roll > 0.85) kind = 'join';
      const item = {
        id: ++idRef.current,
        user,
        kind,
        text: pick(COMMENTS, Math.floor(Math.random() * 10000)),
        superText: pick(SUPER_TEXTS, Math.floor(Math.random() * 10000)),
        amount: pick(SUPER_AMOUNTS, Math.floor(Math.random() * 10000)),
      };
      setItems(prev => [...prev, item].slice(-max));
      timer = setTimeout(push, rnd(everySec * 600, everySec * 1600));
    };
    timer = setTimeout(push, 900);
    return () => clearTimeout(timer);
  }, [everySec, max, enabled, kinds.join(',')]);   // eslint-disable-line react-hooks/exhaustive-deps
  return items;
}

/** Spettatori che oscillano come in una diretta vera. */
function useViewers(initial = 1523) {
  const [n, setN] = useState(initial);
  useEffect(() => {
    const t = setInterval(() => setN(v => Math.max(12, Math.round(v + rnd(-6, 11)))), 2600);
    return () => clearInterval(t);
  }, []);
  return n;
}
const shortCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1).replace('.', ',')}k` : String(n));
const fullCount = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/* Icone: disegnate a mano per restare fedeli alle app, senza dipendenze. */
const HeartGlyph = ({ className = '' }) => (
  <svg className={`sc-heart-glyph ${className}`} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.7 1.1-1a5.5 5.5 0 0 0 0-7.7z" />
  </svg>
);

const Ic = {
  Eye: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12 5c-5 0-9 4.5-9 7s4 7 9 7 9-4.5 9-7-4-7-9-7zm0 11.5A4.5 4.5 0 1 1 16.5 12 4.5 4.5 0 0 1 12 16.5zm0-7a2.5 2.5 0 1 0 2.5 2.5A2.5 2.5 0 0 0 12 9.5z" /></svg>),
  Close: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" {...p}><path d="M5 5l14 14M19 5L5 19" /></svg>),
  Chevron: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M7 10l5 5 5-5z" /></svg>),
  Dots: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>),
  Send: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" {...p}><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>),
  Heart: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" {...p}><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.7 1.1-1a5.5 5.5 0 0 0 0-7.7z" /></svg>),
  Question: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><rect x="3" y="3" width="18" height="18" rx="4" /><path d="M9.2 9.3a2.8 2.8 0 1 1 3.6 2.7c-.6.2-.9.7-.9 1.3v.4" strokeLinecap="round" /><circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" /></svg>),
  Play: (p) => (<svg viewBox="0 0 36 36" fill="currentColor" {...p}><path d="M12 26V10l14 8-14 8z" /></svg>),
  Next: (p) => (<svg viewBox="0 0 36 36" fill="currentColor" {...p}><path d="M12 26l10-8-10-8v16zm12-16v16h2V10h-2z" /></svg>),
  Volume: (p) => (<svg viewBox="0 0 36 36" fill="currentColor" {...p}><path d="M8 14v8h5l6 5V9l-6 5H8zm14.5 4a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zm-2.5-8.7v2.1a7 7 0 0 1 0 13.2v2.1a9 9 0 0 0 0-17.4z" /></svg>),
  Gear: (p) => (<svg viewBox="0 0 36 36" fill="currentColor" {...p}><path d="M18 22.5a4.5 4.5 0 1 1 4.5-4.5 4.5 4.5 0 0 1-4.5 4.5zm9-3.2v-2.6l2.4-1.9-2.4-4.1-2.9 1.2a9.6 9.6 0 0 0-2.2-1.3L21.4 7h-4.8l-.5 3.6a9.6 9.6 0 0 0-2.2 1.3L11 10.7l-2.4 4.1L11 16.7v2.6l-2.4 1.9L11 25.3l2.9-1.2a9.6 9.6 0 0 0 2.2 1.3l.5 3.6h4.8l.5-3.6a9.6 9.6 0 0 0 2.2-1.3l2.9 1.2 2.4-4.1z" /></svg>),
  Mini: (p) => (<svg viewBox="0 0 36 36" fill="currentColor" {...p}><path d="M25 17h-8v6h8v-6zm4-9H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h22a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm0 18H7V10h22v16z" /></svg>),
  Theater: (p) => (<svg viewBox="0 0 36 36" fill="currentColor" {...p}><path d="M28 11H8v14h20V11zm2-3H6a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h24a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1z" /></svg>),
  Full: (p) => (<svg viewBox="0 0 36 36" fill="currentColor" {...p}><path d="M10 16H6v-6h6v4h-2v2zm16 0h-2v-2h-2v-4h6v6h-2zM6 20h4v2h2v4H6v-6zm18 2h2v-2h4v6h-6v-4z" /></svg>),
  ThumbUp: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" {...p}><path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3zm3.3-7L13 3a2 2 0 0 1 2 2.3L14.5 9H20a2 2 0 0 1 2 2.4l-1.4 7A2 2 0 0 1 18.6 20H7V10l3.3-7z" /></svg>),
  ThumbDown: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" {...p}><path d="M17 14V3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-3zm-3.3 7L11 21a2 2 0 0 1-2-2.3L9.5 15H4a2 2 0 0 1-2-2.4l1.4-7A2 2 0 0 1 5.4 4H17v10l-3.3 7z" /></svg>),
  Share: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" {...p}><path d="M14 9V5l8 7-8 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5.5 4.2-9.9 11-11z" /></svg>),
  Save: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" {...p}><path d="M14 10H3M14 6H3M10 14H3M17 21v-9l4 4.5" /></svg>),
  Bell: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12 22a2.5 2.5 0 0 0 2.5-2.5h-5A2.5 2.5 0 0 0 12 22zm7-6v-5a7 7 0 0 0-5.5-6.8V3.5a1.5 1.5 0 0 0-3 0v.7A7 7 0 0 0 5 11v5l-2 2v1h18v-1z" /></svg>),
  Emoji: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}><circle cx="12" cy="12" r="9" /><circle cx="9" cy="10" r="1.2" fill="currentColor" stroke="none" /><circle cx="15" cy="10" r="1.2" fill="currentColor" stroke="none" /><path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" strokeLinecap="round" /></svg>),
  Back: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M15 5l-7 7 7 7" /></svg>),
  Cast: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" {...p}><path d="M3 17a4 4 0 0 1 4 4M3 13a8 8 0 0 1 8 8M3 5h18v14h-7" /></svg>),
  DotsV: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><circle cx="12" cy="5" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="12" cy="19" r="1.9" /></svg>),
};

/* ---------------------------------------------------------------------------
   Blocco grafiche RaceSense (regia automatica + sponsor) usato da tutte le
   dirette simulate: occupa tutta la larghezza nella fascia bassa.
--------------------------------------------------------------------------- */
function RaceBlock({ graphics, ticker, rows = 5, interval = 9000, className = '' }) {
  if (!graphics && !ticker) return null;
  return (
    <div className={`sc-race ${className}`}>
      {graphics && (
        <div className="sc-race__graphics">
          <AutoGraphics rows={rows} interval={interval} />
        </div>
      )}
      {ticker && (
        <div className="sc-race__ticker">
          <SponsorTicker speed={26} />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════ INSTAGRAM LIVE ═══════════════════════════ */
function InstagramLive({ user, viewers, chat, graphics, ticker, scrim }) {
  return (
    <div className={`ig ${scrim ? 'is-scrim' : ''}`}>
      <div className="ig-top">
        <div className="ig-top__left">
          <span className="ig-ring"><img src={raceSenseLogo} alt="" /></span>
          <span className="ig-user">{user}</span>
          <Ic.Chevron className="ig-user__caret" />
        </div>
        <div className="ig-top__right">
          <span className="ig-live">LIVE</span>
          <span className="ig-viewers"><Ic.Eye className="ig-viewers__eye" />{shortCount(viewers)}</span>
          <Ic.Close className="ig-close" />
        </div>
      </div>

      <div className={`ig-bottom ${chat.length ? '' : 'is-nocomments'}`}>
        {chat.length > 0 && (
        <div className="ig-comments">
          {chat.map(c => (
            <div className={`ig-comment ig-comment--${c.kind}`} key={c.id}>
              {c.kind === 'join' ? (
                <span className="ig-join"><Avatar name={c.user} size={22} />{c.user} si è unito/a</span>
              ) : (
                <>
                  <Avatar name={c.user} size={32} />
                  <span className="ig-comment__body">
                    {c.kind === 'badge' ? (
                      <span className="ig-badge-msg"><strong>{c.user}</strong> ha comprato un badge <span className="ig-badge-hearts"><HeartGlyph /><HeartGlyph /></span></span>
                    ) : (
                      <>
                        <strong>{c.user}</strong>
                        <span>{c.text}</span>
                      </>
                    )}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
        )}

        <RaceBlock graphics={graphics} ticker={ticker} rows={4} className="sc-race--ig" />

        <div className="ig-badgebar">
          <span>Compra un badge per sostenere <strong>{user}</strong></span>
          <span className="ig-badgebar__btn">Compra</span>
        </div>

        <div className="ig-inputbar">
          <span className="ig-input">Commenta<Ic.Dots className="ig-input__dots" /></span>
          <span className="ig-actions">
            <Ic.Question className="ig-action" />
            <Ic.Send className="ig-action" />
            <Ic.Heart className="ig-action" />
          </span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ YOUTUBE — DIRETTA DA PC ═══════════════════════ */
function YouTubeDesktop({ channel, title, viewers, chat, graphics, ticker, scrim }) {
  return (
    <div className="yt">
      <div className="yt-main">
        <div className={`yt-player ${scrim ? 'is-scrim' : ''}`}>
          <RaceBlock graphics={graphics} ticker={ticker} rows={6} className="sc-race--yt" />
          <div className="yt-controls">
            <div className="yt-progress"><span /></div>
            <div className="yt-controls__row">
              <Ic.Play className="yt-ctl" />
              <Ic.Next className="yt-ctl" />
              <Ic.Volume className="yt-ctl" />
              <span className="yt-livedot"><i />In diretta</span>
              <span className="yt-spacer" />
              <Ic.Gear className="yt-ctl" />
              <Ic.Mini className="yt-ctl" />
              <Ic.Theater className="yt-ctl" />
              <Ic.Full className="yt-ctl" />
            </div>
          </div>
        </div>

        <h1 className="yt-title">{title}</h1>
        <div className="yt-meta">
          <span className="yt-ch">
            <img className="yt-ch__logo" src={raceSenseLogo} alt="" />
            <span className="yt-ch__text">
              <strong>{channel}</strong>
              <em>18,4 mila iscritti</em>
            </span>
            <span className="yt-subscribe">Iscriviti</span>
            <Ic.Bell className="yt-bell" />
          </span>
          <span className="yt-actions">
            <span className="yt-pill yt-pill--split">
              <span className="yt-pill__half"><Ic.ThumbUp className="yt-ico" />1,2 mila</span>
              <span className="yt-pill__sep" />
              <span className="yt-pill__half"><Ic.ThumbDown className="yt-ico" /></span>
            </span>
            <span className="yt-pill"><Ic.Share className="yt-ico" />Condividi</span>
            <span className="yt-pill"><Ic.Save className="yt-ico" />Salva</span>
          </span>
        </div>
        <div className="yt-desc">
          <strong>{fullCount(viewers)} persone stanno guardando ora</strong>
          <span> · Iniziata 2 ore fa · #racesense #karting #livetiming</span>
        </div>
      </div>

      <aside className="yt-chat">
        <div className="yt-chat__head">
          <span>Chat dal vivo</span>
          <Ic.DotsV className="yt-chat__dots" />
        </div>
        <div className="yt-chat__list">
          {chat.map(c => (
            c.kind === 'super' ? (
              <div className="yt-super" key={c.id}>
                <div className="yt-super__head">
                  <Avatar name={c.user} size={24} />
                  <span className="yt-super__user">{c.user}</span>
                  <span className="yt-super__amount">{c.amount}</span>
                </div>
                <div className="yt-super__text">{c.superText}</div>
              </div>
            ) : (
              <div className="yt-msg" key={c.id}>
                <Avatar name={c.user} size={24} />
                <span className="yt-msg__user">{c.user}</span>
                <span className="yt-msg__text">{c.kind === 'join' ? 'è appena entrato in chat' : c.text}</span>
              </div>
            )
          ))}
        </div>
        <div className="yt-chat__foot">
          <Avatar name="tu_spettatore" size={24} />
          <span className="yt-chat__input">Scrivi un messaggio...</span>
          <Ic.Emoji className="yt-chat__emoji" />
        </div>
      </aside>
    </div>
  );
}

/* ═══════════════════ YOUTUBE — DIRETTA DA TELEFONO (9:16) ═══════════════════ */
function YouTubeMobile({ channel, title, viewers, chat, graphics, ticker, scrim }) {
  return (
    <div className={`ytm ${scrim ? 'is-scrim' : ''}`}>
      <div className="ytm-top">
        <Ic.Back className="ytm-top__ico" />
        <span className="ytm-top__right">
          <Ic.Cast className="ytm-top__ico" />
          <Ic.DotsV className="ytm-top__ico" />
        </span>
      </div>

      <div className="ytm-head">
        <img className="ytm-head__logo" src={raceSenseLogo} alt="" />
        <span className="ytm-head__text">
          <strong>{title}</strong>
          <em>{channel} · <span className="ytm-live">IN DIRETTA</span> · {fullCount(viewers)} in ascolto</em>
        </span>
        <span className="ytm-sub">Iscriviti</span>
      </div>

      <div className="ytm-dock">
        <div className="ytm-side">
          <span className="ytm-side__btn"><Ic.ThumbUp />1,2 mila</span>
          <span className="ytm-side__btn"><Ic.ThumbDown />Non mi piace</span>
          <span className="ytm-side__btn"><Ic.Share />Condividi</span>
        </div>
        {chat.length > 0 && (
        <div className="ytm-chat">
        {chat.map(c => (
          c.kind === 'super' ? (
            <div className="ytm-super" key={c.id}>
              <Avatar name={c.user} size={22} />
              <span className="ytm-super__user">{c.user}</span>
              <span className="ytm-super__amount">{c.amount}</span>
              <span className="ytm-super__text">{c.superText}</span>
            </div>
          ) : (
            <div className="ytm-msg" key={c.id}>
              <Avatar name={c.user} size={22} />
              <span className="ytm-msg__user">{c.user}</span>
              <span className="ytm-msg__text">{c.kind === 'join' ? 'è entrato in chat' : c.text}</span>
            </div>
          )
          ))}
        </div>
        )}
        <RaceBlock graphics={graphics} ticker={ticker} rows={4} className="sc-race--ytm" />
        <div className="ytm-input">
          <Avatar name="tu_spettatore" size={22} />
          <span className="ytm-input__field">Scrivi un messaggio...</span>
          <Ic.Emoji className="ytm-input__ico" />
          <Ic.Send className="ytm-input__ico" />
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Pagina
--------------------------------------------------------------------------- */
export default function SocialOverlay({ network: fixedNetwork }) {
  const { network: routeNetwork } = useParams();
  const [params] = useSearchParams();
  const network = String(fixedNetwork || routeNetwork || 'instagram').toLowerCase();
  const vertical = network !== 'youtube';       // youtube = 16:9, gli altri 9:16

  const bg = (params.get('bg') || 'green').toLowerCase();
  const user = params.get('user') || 'racesense_official';
  const channel = params.get('channel') || 'RaceSense Live';
  const title = params.get('title') || 'GARA ENDURANCE 12H · LIVE TIMING RACESENSE';
  const graphics = params.get('graphics') !== '0';
  const ticker = params.get('ticker') !== '0';
  const scrim = params.get('scrim') === '1';
  // Commenti finti: accesi solo sulla diretta YouTube da PC, dove la chat fa
  // parte dell'interfaccia. Nelle verticali la scena resta libera.
  const everySec = params.get('comments') != null ? Number(params.get('comments')) : (network === 'youtube' ? 3 : 0);
  const viewers = useViewers(Math.max(1, Number(params.get('viewers')) || 1523));
  const kinds = network === 'instagram' ? ['msg', 'join', 'badge'] : ['msg', 'join', 'super'];
  const chat = useFakeChat({
    everySec,
    max: network === 'youtube' ? 14 : 4,
    kinds,
    enabled: everySec > 0,
    seed: network === 'youtube' ? 10 : 4,
  });

  // Fondo pagina: verde per il chroma key (o trasparente/scuro su richiesta).
  useEffect(() => {
    const color = bg === 'green' ? '#00ff00' : bg === 'dark' ? '#0a0a0a' : 'transparent';
    const html = document.documentElement, body = document.body;
    const prev = { h: html.style.background, b: body.style.background };
    html.style.background = color; body.style.background = color;
    return () => { html.style.background = prev.h; body.style.background = prev.b; };
  }, [bg]);

  const common = { viewers, chat, graphics, ticker, scrim };
  return (
    <div className={`sc-root sc-root--${bg}`}>
      <Stage w={vertical ? IG_W : YT_W} h={vertical ? IG_H : YT_H} className={`sc-stage--${network}`}>
        {network === 'instagram' && <InstagramLive user={user} {...common} />}
        {network === 'youtube' && <YouTubeDesktop channel={channel} title={title} {...common} />}
        {network !== 'instagram' && network !== 'youtube' && <YouTubeMobile channel={channel} title={title} {...common} />}
      </Stage>
    </div>
  );
}
